import { useState } from 'react';
import styled from 'styled-components';

import { colors } from '/css';
import Modal from '/components/ui/Modal';
import Heading from '/components/ui/Heading';
import LegacyInput from '../ui/LegacyInput';
import Button from '/components/ui/buttons/Button';
import Paragraph from '/components/ui/Paragraph';
import projectApi from '/api/project';
import orderApi from '/api/order';
import offerApi from '/api/offer';
import { useOrderState } from '/store/orderStore';

const DiscountCodeModal = ({ resource, resourceType, onCodeApplied }) => {
    const cart = useOrderState((state) => state.cart);
    const setOrderCart = useOrderState((state) => state.setOrderCart);

    const [discountCodePopupVisible, setDiscountCodePopupVisibility] =
        useState(false);
    const [discountCode, setDiscountCode] = useState('');
    const [checkingCode, setCheckingCodeValue] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);

    const showDiscountPopup = (e) => {
        e.preventDefault();
        setDiscountCodePopupVisibility(true);
    };

    const hideDiscountPopup = () => {
        setDiscountCodePopupVisibility(false);
        setDiscountCode('');
        setErrorMessage(null);
    };

    const applyDiscount = async () => {
        if (checkingCode || !discountCode) return;

        setCheckingCodeValue(true);

        const resp = await projectApi.checkDiscountCode(discountCode);

        if (resp.error) {
            setErrorMessage(resp.error);
            setCheckingCodeValue(false);
            return;
        }

        setCheckingCodeValue(false);
        setDiscountCodePopupVisibility(false);

        const payload = {
            type: 'DISCOUNT_CODE',
            data: { code: resp.code },
        };

        switch (resourceType) {
            case 'cart':
                if (!cart) return;
                await orderApi.cart.lineItem.create(cart.uuid, payload);
                setOrderCart((await orderApi.cart.get(cart.uuid)).data);
                break;
            case 'manual-cart':
                await orderApi.cart.lineItem.create(resource.uuid, payload);
                break;
            case 'project':
                await projectApi.createLineItem(resource.id, payload);
                break;
            case 'offer':
                await offerApi.createLineItem(resource.id, payload);
                break;
        }

        if (onCodeApplied) onCodeApplied(resp);
    };

    const discountApplied = (resource || cart)?.lineItems?.filter(
        (item) => item.type === 'DISCOUNT_CODE'
    )[0];

    return (
        <>
            {discountCodePopupVisible && (
                <Modal
                    closeRequestCallback={hideDiscountPopup}
                    className="p-32"
                >
                    <Heading medium className="mb-32">
                        Rabattkode
                    </Heading>
                    <div style={{ position: 'relative' }}>
                        <LegacyInput
                            onChange={(val) => setDiscountCode(val)}
                            value={discountCode}
                            name="discountCode"
                            placeholder="Kode"
                            rules={{ required: true }}
                            onKeyPress={(e) =>
                                e.key === 'Enter' ? applyDiscount() : null
                            }
                            className="mb-32"
                        />
                        {errorMessage && (
                            <ErrorEl id="code-error">
                                <svg
                                    width="10px"
                                    height="10px"
                                    viewBox="0 0 10 10"
                                    version="1.1"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <g
                                        stroke="none"
                                        strokeWidth="1"
                                        fill="none"
                                        fillRule="evenodd"
                                    >
                                        <g
                                            transform="translate(-1110.000000, -262.000000)"
                                            stroke="#FF4400"
                                            strokeWidth="2"
                                        >
                                            <g transform="translate(1111.000000, 263.000000)">
                                                <path d="M8,0 L0,8"></path>
                                                <path
                                                    d="M8,1.13686838e-13 L0,8"
                                                    transform="translate(4.000000, 4.000000) scale(-1, 1) translate(-4.000000, -4.000000) "
                                                ></path>
                                            </g>
                                        </g>
                                    </g>
                                </svg>
                                Ikke gyldig
                            </ErrorEl>
                        )}
                    </div>
                    <Button
                        size="medium"
                        variant="tertiary"
                        onClick={applyDiscount}
                        busy={checkingCode}
                    >
                        Legg til
                    </Button>
                </Modal>
            )}
            {!discountApplied && (
                <Paragraph medium>
                    <a
                        id="code-popup-link"
                        style={{ color: colors.purple }}
                        href="#"
                        onClick={showDiscountPopup}
                    >
                        Har du rabattkode?
                    </a>
                </Paragraph>
            )}
        </>
    );
};

export default DiscountCodeModal;

const ErrorEl = styled.div`
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    right: 18px;
    display: flex;
    align-items: center;
    color: ${colors.orange};
    line-height: 1;
    font-family: 'gellix-semi';
    svg {
        margin-right: 8px;
        margin-top: 2px;
    }
`;
